import { useState } from "react";
import { ClickAwayListener, Grid, IconButton, List, ListItem, Stack, TextField, Tooltip, Typography, Zoom } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddCircleOutline } from "@mui/icons-material";

interface Props<T> {
  /**
   * Title to display above the list
   */
  title?: string;
  /**
   * List items to display
   */
  items: T[];
  /**
   * Function used to render the content of the list.
   * Must map an object instance (element in prop `items`) to a ReactElement
   */
  renderItem: (item: T, index: number) => JSX.Element;
  /**
   * Callback fired when an item instance is added
   */
  onAdd?: (newItem: string) => void;
  /**
   * Callback fired when an item instance is deleted
   */
  onDelete?: (itemIndex: number) => void;
  /**
   * Icon for each list component
   */
  icon?: JSX.Element;
  /**
   * Function to execute when click on icon button
   */
  handleOnClick?: () => void;
  /**
   * Tooltip to show when click on icon button
   */
  toolTipText?: string;
  /**
   * Input name attribute
   */
  name?: string;
}

export function ListComponentWithIcon<T>({ title, items, renderItem, onAdd, onDelete, name, icon, handleOnClick, toolTipText }: Props<T>) {
  const [newItemValue, setNewItemValue] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);

  if (!toolTipText && icon) {
    toolTipText = "Copied to clipboard!";
  }

  const handleOnAdd = () => {
    if (!newItemValue || !onAdd) {
      return;
    }

    onAdd(newItemValue);
    setNewItemValue(undefined);
  };

  const handleOnDelete = (index: number) => {
    if (!onDelete) {
      return;
    }

    onDelete(index);
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    setTimeout(() => setOpen(false), 1500);
  };

  return (
    <>
      <Stack direction="row" spacing={1} style={{ paddingTop: 15, paddingBottom: 15}}>
        {title && <Typography sx={{ mt: 4, mb: 2 }} variant="h6" component="div" children={title} style={{ fontSize: "15px" }} />}
        {icon && (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
              PopperProps={{
                disablePortal: false,
              }}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              placement={"right"}
              TransitionComponent={Zoom}
              title={toolTipText!}
              arrow
            >
              <IconButton
                children={icon}
                size="small"
                style={{ marginTop: "0rem", marginLeft: "0rem", height: "fit-content" }}
                onClick={() => {
                  handleTooltipOpen();
                  handleOnClick!();
                }}
                sx={{
                  color: "#D3D3D3",
                  transition: "0.3s",
                  "&:hover": { color: "black", transition: "0.3s" },
                }}
              ></IconButton>
            </Tooltip>
          </ClickAwayListener>
        )}
      </Stack>
      { items.length !== 0 ? 
            <List>
            {items.map((item, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  onDelete ? (
                    <IconButton edge="end" aria-label="delete" onClick={() => handleOnDelete(index)}>
                      <DeleteIcon />
                    </IconButton>
                  ) : undefined
                }
              >
                {renderItem(item, index)}
              </ListItem>
            ))}
          </List>
      : "" }


      {onAdd && (
        <Grid container display={"flex"} alignItems={"center"} justifyItems={"center"}>
          <Grid item xs={11}>
            <TextField
              id="filled-basic"
              label="Add a new item"
              name={name ?? ""}
              variant="filled"
              value={newItemValue ?? ""}
              onChange={(e) => setNewItemValue(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleOnAdd();
                }
              }}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton
              onClick={handleOnAdd}
              size={"large"}
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}
            >
              <AddCircleOutline />
            </IconButton>
          </Grid>
        </Grid>
      )}
    </>
  );
}
